/**
 * Hook für die Seiten "Anträge" und "Antrag": Genehmigungsverfahren, Antragsbausteine und
 * die Nachweise im Baum der Bausteine (anlegen, zuordnen, Zuordnung lösen – alles über die
 * Auswahl der Baustein-Typen am Nachweis).
 *
 * Bearbeitet, durch den Ablauf geführt und gelöscht werden Nachweise auf der Seite "Nachweise"
 * (`useProjectEvidences`), Kosten auf der Seite "Kosten" – beides steht deshalb nicht hier.
 */
import { useState, useCallback, useEffect, useMemo } from "react";
import { permitsApi } from "../../../../services/api/permits";
import { projectsApi } from "../../../../services/api/projects";
import { generationUnitsApi } from "../../../../services/api/generationUnits";
import { parseApiError } from "../../../../services/api/errorPresentation";
import {
  hasAnyFieldChanged,
  hasListChanged,
} from "../../../../utils/formUtils";
import { useSaveState } from "../../../../hooks/useSaveState";
import { usePermitLookups } from "./usePermitLookups";
import {
  AnlageOption,
  AntragVorbelegung,
  ApplicationComponent,
  ApplicationComponentCategoryOption,
  ApplicationComponentCreate,
  ApplicationComponentTypeOption,
  ApplicationComponentUpdate,
  StandortmerkmalOption,
  WindenergiegebietOption,
  Evidence,
  EvidenceCreate,
  LookupOption,
  PermitProcedure,
  PermitProcedureCreate,
  PermitProcedureUpdate,
  ProcedureTransition,
  UvpStatusOption,
} from "../../../../types";

/**
 * Hat das Teil-Update am Antrag überhaupt etwas geändert? Dirty-Check vor dem PATCH nach
 * docs/adr/004_autosave_dirty_check.md – ohne ihn löst jedes Verlassen eines Feldes einen
 * Request und damit einen "Gespeichert"-Toast aus, auch beim bloßen Durchtabben.
 *
 * Vorhabenumfang und Besonderheiten des Standorts laufen über `hasListChanged`: sie stehen am
 * Antrag als Objektlisten und werden hier zu IDs aufgelöst, alles Übrige geht an
 * `hasAnyFieldChanged`.
 */
const antragHatAenderung = (
  bisher: PermitProcedure,
  data: PermitProcedureUpdate,
): boolean => {
  const {
    vorhabenbestandteil_ids: neueIds,
    anlage_ids: neueAnlageIds,
    standortmerkmal_ids: neueMerkmalIds,
    ...uebrige
  } = data;

  if (
    neueIds &&
    hasListChanged(
      (bisher.vorhabenbestandteile ?? []).map((bestandteil) => bestandteil.id),
      neueIds,
    )
  ) {
    return true;
  }
  if (neueAnlageIds && hasListChanged(bisher.anlage_ids ?? [], neueAnlageIds)) {
    return true;
  }
  if (
    neueMerkmalIds &&
    hasListChanged(
      (bisher.standortmerkmale ?? []).map((merkmal) => merkmal.id),
      neueMerkmalIds,
    )
  ) {
    return true;
  }

  return Object.keys(uebrige).length > 0 && hasAnyFieldChanged(bisher, uebrige);
};

interface UsePermitsDataProps {
  projectId: string | undefined;
}

export function usePermitsData({ projectId }: UsePermitsDataProps) {
  const [procedures, setProcedures] = useState<PermitProcedure[]>([]);
  const [selectedProcedureId, setSelectedProcedureId] = useState<string | null>(
    null,
  );
  const [components, setComponents] = useState<ApplicationComponent[]>([]);
  const [evidences, setEvidences] = useState<Evidence[]>([]);

  const [procedureTypeOptions, setProcedureTypeOptions] = useState<
    LookupOption[]
  >([]);
  const [componentCategoryOptions, setComponentCategoryOptions] = useState<
    ApplicationComponentCategoryOption[]
  >([]);
  const [componentTypeOptions, setComponentTypeOptions] = useState<
    ApplicationComponentTypeOption[]
  >([]);
  // Dokumentarten, Entstehungsarten und Nutzer teilt sich diese Seite mit den beiden
  // Übersichtsseiten des Moduls.
  const {
    evidenceTypeOptions,
    processingPathOptions,
    nachweisKatalog,
    orgUsers,
    loading: lookupsLoading,
  } = usePermitLookups();

  // Angaben, die außerhalb des Verfahrens gepflegt werden
  const [uvpStatusOptions, setUvpStatusOptions] = useState<UvpStatusOption[]>(
    [],
  );
  const [vorhabenbestandteilOptions, setVorhabenbestandteilOptions] = useState<
    LookupOption[]
  >([]);
  const [standortmerkmalOptions, setStandortmerkmalOptions] = useState<
    StandortmerkmalOption[]
  >([]);
  const [windenergiegebietOptions, setWindenergiegebietOptions] = useState<
    WindenergiegebietOption[]
  >([]);
  /** Vorbelegung des Anlegeformulars – vom Server, nach der Regel `regeln/vorbelegung_antrag.py` */
  const [vorbelegung, setVorbelegung] = useState<AntragVorbelegung | null>(
    null,
  );
  /** Die Anlagen des Projekts als Auswahl für den Antragsgegenstand */
  const [anlageOptionen, setAnlageOptionen] = useState<AnlageOption[]>([]);

  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Speicher-Zustand aller Schreibvorgänge dieser Seite; PageTemplate macht daraus den Toast.
  const { isSaving, saveError, speichern } = useSaveState();

  const loadProcedures = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [
        proceduresData,
        procedureTypes,
        categories,
        componentTypes,
        uvpStatuses,
        bestandteilOptionen,
        merkmalOptionen,
        gebietOptionen,
        anlagen,
        vorbelegungDaten,
      ] = await Promise.all([
        permitsApi.getPermitProcedures(projectId),
        permitsApi.getProcedureTypeOptions(),
        permitsApi.getApplicationComponentCategoryOptions(),
        permitsApi.getApplicationComponentTypeOptions(),
        permitsApi.getUvpStatusOptions(),
        permitsApi.getVorhabenbestandteilOptions(),
        permitsApi.getStandortmerkmalOptions(),
        permitsApi.getWindenergiegebietOptions(),
        // Die Anlagen als Auswahl für den Antragsgegenstand.
        generationUnitsApi
          .getProjectGenerationUnits(projectId)
          .catch(() => null),
        permitsApi.getAntragVorbelegung(projectId).catch(() => null),
      ]);
      const loadedProcedures = proceduresData ?? [];
      setProcedures(loadedProcedures);
      // Welcher Antrag offen ist, bestimmt die Seite aus der URL; ein gelöschter fällt heraus.
      setSelectedProcedureId((prev) =>
        prev && loadedProcedures.some((p) => p.id === prev) ? prev : null,
      );
      setProcedureTypeOptions(procedureTypes ?? []);
      setComponentCategoryOptions(categories ?? []);
      setComponentTypeOptions(componentTypes ?? []);
      setUvpStatusOptions(uvpStatuses ?? []);
      setVorhabenbestandteilOptions(bestandteilOptionen ?? []);
      setStandortmerkmalOptions(merkmalOptionen ?? []);
      setWindenergiegebietOptions(gebietOptionen ?? []);
      setVorbelegung(vorbelegungDaten);
      setAnlageOptionen(
        (anlagen ?? []).map((anlage) => ({
          id: anlage.id,
          bezeichnung:
            anlage.bezeichnung || anlage.anlagen_nr || "(ohne Bezeichnung)",
        })),
      );
    } catch (err) {
      setError(parseApiError(err).message);
      setProcedures([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadProcedures();
  }, [loadProcedures]);

  const loadProcedureDetails = useCallback(
    async (permitProcedureId: string) => {
      if (!projectId) return;
      setDetailLoading(true);
      try {
        const [componentsData, evidencesData] = await Promise.all([
          permitsApi.getApplicationComponents(projectId, permitProcedureId),
          permitsApi.getEvidences(projectId, permitProcedureId),
        ]);
        setComponents(componentsData ?? []);
        setEvidences(evidencesData ?? []);
      } catch (err) {
        setError(parseApiError(err).message);
        setComponents([]);
        setEvidences([]);
      } finally {
        setDetailLoading(false);
      }
    },
    [projectId],
  );

  useEffect(() => {
    if (selectedProcedureId) {
      loadProcedureDetails(selectedProcedureId);
    } else {
      setComponents([]);
      setEvidences([]);
    }
  }, [selectedProcedureId, loadProcedureDetails]);

  const selectedProcedure =
    procedures.find((p) => p.id === selectedProcedureId) ?? null;

  const createProcedure = useCallback(
    async (data: PermitProcedureCreate): Promise<PermitProcedure> => {
      if (!projectId) throw new Error("Keine Projekt-ID");
      const created = await permitsApi.createPermitProcedure(projectId, data);
      setProcedures((prev) => [...prev, created]);
      setSelectedProcedureId(created.id);
      // Der nächste Antrag übernimmt vom eben angelegten und ist nicht mehr der erste.
      setVorbelegung(
        await permitsApi.getAntragVorbelegung(projectId).catch(() => null),
      );
      return created;
    },
    [projectId],
  );

  /** UVP-Status für eine geänderte Anlagenauswahl – dieselbe Regel wie die Vorbelegung */
  const uvpVorbelegungFuer = useCallback(
    async (anlagenzahl: number): Promise<string | null> => {
      if (!projectId) return null;
      const antwort = await permitsApi
        .getAntragVorbelegung(projectId, anlagenzahl)
        .catch(() => null);
      return antwort?.uvp_status_id ?? null;
    },
    [projectId],
  );

  /**
   * Speichert einzelne Felder des Antrags (Blur-Pattern). Meldet Erfolg und Fehler über
   * `isSaving`/`saveError` an `PageTemplate` und wirft deshalb nicht.
   */
  const updateProcedure = useCallback(
    async (
      permitProcedureId: string,
      data: PermitProcedureUpdate,
    ): Promise<void> => {
      if (!projectId) return;
      const bisher = procedures.find((p) => p.id === permitProcedureId);
      if (bisher && !antragHatAenderung(bisher, data)) return;

      await speichern(async () => {
        const updated = await permitsApi.updatePermitProcedure(
          projectId,
          permitProcedureId,
          data,
        );
        setProcedures((prev) =>
          prev.map((p) => (p.id === permitProcedureId ? updated : p)),
        );
      });
    },
    [projectId, procedures, speichern],
  );

  /**
   * Führt eine Aktion am Antrag aus (ADR 011). Fehler meldet `PageTemplate`; `null` heißt, der
   * Server hat abgelehnt – der Dialog bleibt dann offen.
   */
  const transitionProcedure = useCallback(
    async (
      permitProcedureId: string,
      data: ProcedureTransition,
    ): Promise<PermitProcedure | null> => {
      if (!projectId) return null;
      return speichern(async () => {
        const updated = await permitsApi.transitionPermitProcedure(
          projectId,
          permitProcedureId,
          data,
        );
        setProcedures((prev) =>
          prev.map((p) => (p.id === permitProcedureId ? updated : p)),
        );
        return updated;
      });
    },
    [projectId, speichern],
  );

  /** Takes over a procedure changed elsewhere (a transition in the page header) */
  const replaceProcedure = useCallback((updated: PermitProcedure) => {
    setProcedures((prev) =>
      prev.map((p) => (p.id === updated.id ? updated : p)),
    );
  }, []);

  const deleteProcedure = useCallback(
    async (permitProcedureId: string): Promise<void> => {
      if (!projectId) throw new Error("Keine Projekt-ID");
      await permitsApi.deletePermitProcedure(projectId, permitProcedureId);
      setProcedures((prev) => prev.filter((p) => p.id !== permitProcedureId));
      setSelectedProcedureId((prev) =>
        prev === permitProcedureId ? null : prev,
      );
    },
    [projectId],
  );

  const createComponent = useCallback(
    async (data: ApplicationComponentCreate): Promise<ApplicationComponent> => {
      if (!projectId || !selectedProcedureId)
        throw new Error("Kein Genehmigungsverfahren ausgewählt");
      const created = await permitsApi.createApplicationComponent(
        projectId,
        selectedProcedureId,
        data,
      );
      setComponents((prev) => [...prev, created]);
      return created;
    },
    [projectId, selectedProcedureId],
  );

  /**
   * Aktualisiert einen Antragsbaustein, insbesondere seine Notwendigkeit.
   *
   * Das Kennzeichen "manuell geändert" wird bewusst nicht mitgesendet – der Server setzt es
   * selbst, sobald die Notwendigkeit tatsächlich von der bisherigen abweicht.
   *
   * Ohne Dirty-Check: ausgelöst wird das durch einen Schalter, nicht durch Durchtabben – der
   * Klick ist immer eine Änderung (ADR 004, Entscheidung 4).
   */
  const updateComponent = useCallback(
    async (
      componentId: string,
      data: ApplicationComponentUpdate,
    ): Promise<void> => {
      if (!projectId || !selectedProcedureId) return;
      await speichern(async () => {
        const updated = await permitsApi.updateApplicationComponent(
          projectId,
          selectedProcedureId,
          componentId,
          data,
        );
        setComponents((prev) =>
          prev.map((component) =>
            component.id === componentId ? updated : component,
          ),
        );
      });
    },
    [projectId, selectedProcedureId, speichern],
  );

  const deleteComponent = useCallback(
    async (componentId: string): Promise<void> => {
      if (!projectId || !selectedProcedureId)
        throw new Error("Kein Genehmigungsverfahren ausgewählt");
      await permitsApi.deleteApplicationComponent(
        projectId,
        selectedProcedureId,
        componentId,
      );
      setComponents((prev) => prev.filter((c) => c.id !== componentId));
      // Die Nachweise bleiben am Antrag, sie verlieren nur diesen Baustein (ADR 006).
      setEvidences((prev) =>
        prev.map((e) => ({
          ...e,
          application_components: e.application_components.filter(
            (ref) => ref.id !== componentId,
          ),
        })),
      );
    },
    [projectId, selectedProcedureId],
  );

  /**
   * Lädt die Bausteine des Antrags neu. Nötig nach jeder Zuordnung: Wählt man einen Baustein,
   * der noch nicht im Antrag ist, legt der Server ihn dabei an.
   */
  const ladeBausteine = useCallback(async () => {
    if (!projectId || !selectedProcedureId) return;
    setComponents(
      (await permitsApi.getApplicationComponents(
        projectId,
        selectedProcedureId,
      )) ?? [],
    );
  }, [projectId, selectedProcedureId]);

  const createEvidence = useCallback(
    async (data: EvidenceCreate): Promise<Evidence> => {
      if (!projectId || !selectedProcedureId)
        throw new Error("Kein Genehmigungsverfahren ausgewählt");
      const created = await permitsApi.createEvidence(
        projectId,
        selectedProcedureId,
        data,
      );
      setEvidences((prev) => [...prev, created]);
      if (data.application_component_type_ids?.length) await ladeBausteine();
      return created;
    },
    [projectId, selectedProcedureId, ladeBausteine],
  );

  /**
   * Setzt die Bausteine, die einen Nachweis benötigen – derselbe Weg wie im Nachweis-Detail
   * (`application_component_type_ids`). Zuordnen und Lösen im Baum sind beides nur eine
   * geänderte Auswahl.
   */
  const setzeBausteine = useCallback(
    async (evidenceId: string, typeIds: string[]): Promise<void> => {
      if (!projectId || !selectedProcedureId) return;
      await speichern(async () => {
        const updated = await permitsApi.updateEvidence(
          projectId,
          selectedProcedureId,
          evidenceId,
          { application_component_type_ids: typeIds },
        );
        setEvidences((prev) =>
          prev.map((e) => (e.id === evidenceId ? updated : e)),
        );
        await ladeBausteine();
      });
    },
    [projectId, selectedProcedureId, speichern, ladeBausteine],
  );

  /**
   * Nachweise je Baustein – abgeleitet aus den Nachweisen des Antrags, die ihre benötigenden
   * Bausteine selbst nennen. Ein Nachweis steht unter jedem Baustein, der ihn benötigt.
   */
  const evidencesByComponent = useMemo(() => {
    const result: Record<string, Evidence[]> = {};
    for (const evidence of evidences) {
      for (const ref of evidence.application_components) {
        (result[ref.id] ??= []).push(evidence);
      }
    }
    return result;
  }, [evidences]);

  return {
    procedures,
    selectedProcedure,
    selectedProcedureId,
    selectProcedure: setSelectedProcedureId,
    components,
    evidences,
    evidencesByComponent,
    procedureTypeOptions,
    componentCategoryOptions,
    componentTypeOptions,
    evidenceTypeOptions,
    processingPathOptions,
    nachweisKatalog,
    orgUsers,
    uvpStatusOptions,
    vorhabenbestandteilOptions,
    standortmerkmalOptions,
    windenergiegebietOptions,
    vorbelegung,
    uvpVorbelegungFuer,
    anlageOptionen,
    // Die Seite wartet auch auf die Referenzdaten: das Verfahrensformular belegt sich beim
    // ersten Mount vor und mountet sonst mit leeren Optionslisten.
    loading: loading || lookupsLoading,
    detailLoading,
    error,
    reload: loadProcedures,
    createProcedure,
    updateProcedure,
    transitionProcedure,
    replaceProcedure,
    isSaving,
    saveError,
    speichern,
    deleteProcedure,
    createComponent,
    updateComponent,
    deleteComponent,
    createEvidence,
    setzeBausteine,
  };
}
